<template>
  <a-modal
    title="上传二维码"
    :visible="visible"
    :width="500"
    :mask-closable="false"
    :destroy-on-close="true"
    @cancel="handleCancel"
    @afterClose="handleUploadDialogClosed"
  >
    <a-form-model
      ref="uploadFormRef"
      :model="uploadForm"
      :rules="uploadRules"
      :label-col="{ span: 6 }"
      :wrapper-col="{ span: 18 }"
    >
      <a-form-model-item label="型号名称" prop="deviceModalName">
        <a-select
          v-model="uploadForm.deviceModalName"
          placeholder="请选择型号名称"
          style="width: 100%"
        >
          <a-select-option
            v-for="device in deviceList"
            :key="device.deviceModel"
            :value="device.modelName"
          >
            {{ device.modelName }}
          </a-select-option>
        </a-select>
      </a-form-model-item>

      <a-form-model-item label="二维码文件" prop="file">
        <a-upload
          ref="uploader"
          :file-list="fileList"
          :before-upload="beforeUpload"
          :remove="handleFileRemove"
          accept=".zip,.rar"
        >
          <a-button type="default" icon="upload">选择压缩包</a-button>
        </a-upload>
        <div class="ant-upload-hint" style="font-size: 12px; color: #8c8c8c; margin-top: 4px">
          只能上传 zip/rar 压缩文件，且不超过 50MB
        </div>
      </a-form-model-item>
    </a-form-model>

    <template slot="footer">
      <div class="dialog-footer">
        <a-button @click="handleCancel">取消</a-button>
        <a-button type="primary" :loading="uploading" @click="submitUpload(false)"> 确认 </a-button>
        <a-button
          type="primary"
          :loading="uploading"
          style="background-color: #52c41a; border-color: #52c41a"
          @click="submitUpload(true)"
        >
          确认并下载
        </a-button>
      </div>
    </template>
  </a-modal>
</template>

<script>
import { deviceListApi, uploadQrcode, zip2Json } from "../../../api/index/matter";

export default {
  name: "UploadQrCodeModal",
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    // 自定义文件存在性校验
    const validateFile = (rule, value, callback) => {
      if (!this.uploadForm.file) {
        callback(new Error("请上传二维码文件"));
      } else {
        callback();
      }
    };

    return {
      uploading: false,
      fileList: [],
      deviceList: [],
      uploadForm: {
        deviceModalName: null,
        file: null,
      },
      uploadRules: {
        deviceModalName: [{ required: true, message: "请选择型号名称", trigger: "change" }],
        file: [{ required: true, validator: validateFile, trigger: "change" }],
      },
    };
  },
  watch: {
    visible(val) {
      if (val) {
        this.uploadForm = {
          deviceModalName: null,
          file: null,
        };
        this.fileList = [];
        this.getDeviceList();
      }
    },
  },
  methods: {
    // 拦截默认上传，手动接管 File 对象
    beforeUpload(file) {
      const isZipOrRar = file.name.endsWith(".zip") || file.name.endsWith(".rar");
      if (!isZipOrRar) {
        this.$message.error("仅支持上传 .zip 或 .rar 格式的文件！");
        return false;
      }
      const isLt50M = file.size / 1024 / 1024 < 50;
      if (!isLt50M) {
        this.$message.error("上传文件大小不能超过 50MB！");
        return false;
      }

      // 限制单文件覆盖
      this.fileList = [file];
      this.uploadForm.file = file;

      // 清除文件字段校验红框提示
      this.$refs.uploadFormRef.validateField("file");
      return false;
    },

    handleFileRemove() {
      this.fileList = [];
      this.uploadForm.file = null;
      this.$refs.uploadFormRef.validateField("file");
    },

    handleCancel() {
      this.$emit("update:visible", false);
    },

    handleUploadDialogClosed() {
      this.$refs.uploadFormRef.resetFields();
      this.fileList = [];
      this.uploadForm.file = null;
    },

    async getDeviceList() {
      try {
        const { resultData: response } = await deviceListApi();
        this.deviceList = response || [];
      } catch (error) {
        this.$message.error(error.message || "获取设备型号失败");
      }
    },

    submitUpload(withDownload) {
      this.$refs.uploadFormRef.validate(async (valid) => {
        if (!valid) return;

        this.uploading = true;
        try {
          const formData = new FormData();
          if (this.uploadForm.deviceModalName) {
            const device = this.deviceList.find(
              (item) => item.modelName === this.uploadForm.deviceModalName
            );
            if (!device) throw new Error("未找到对应的设备型号");
            formData.append("deviceModel", device.deviceModel);
            formData.append("modelName", device.modelName);
          }

          if (this.uploadForm.file) {
            formData.append("file", this.uploadForm.file);
          }

          const { resultData: parsedData } = await zip2Json(formData);
          if (!parsedData || parsedData.totalNum <= 0 || !parsedData.items?.length) {
            throw new Error("无有效数据");
          }

          const { resultData: uploadResult } = await uploadQrcode({
            deviceModel: formData.get("deviceModel"),
            modelName: formData.get("modelName"),
            items: parsedData.items,
          });

          const { totalCount, successCount, importedIds = [] } = uploadResult;
          const messageText = `本次上传 ${Math.max(parsedData.totalNum, totalCount)} 条，成功上传${
            withDownload ? "并下载" : ""
          } ${successCount} 条`;

          this.$confirm({
            title: "上传结果",
            content: messageText,
            okText: "重新下载",
            cancelText: "关闭",
            okButtonProps: {
              style: !withDownload ? { display: "none" } : undefined,
              props: {
                disabled: !(withDownload && importedIds.length),
              },
            },
            onOk: () => {
              this.$emit("download", { ids: importedIds });
              this.finishProcess();
            },
            onCancel: () => {
              this.finishProcess();
            },
          });

          if (withDownload && importedIds.length) {
            this.$emit("download", { ids: importedIds });
          }
        } catch (error) {
          console.error("--- error ---", error);
          this.$message.error(error.message || "上传过程中发生异常");
        } finally {
          this.uploading = false;
        }
      });
    },

    finishProcess() {
      this.$emit("update:visible", false);
      this.$emit("uploaded");
    },
  },
};
</script>
